import styles from './WorkspaceOpportunityDetailsSheet.sass'
import { observer } from 'mobx-react'
import { useScrollOffsetSpring } from '../../../../hooks/spring-hooks'
import React from 'react'
import { SideSheet } from '../../../../components/SideSheet'
import cx from 'classnames'
import {
  SideSheetHeader,
  SideSheetHeaderContent,
  SideSheetHeaderAvatar,
  SideSheetHeaderText,
  SideSheetHeaderTitle,
  SideSheetHeaderDescription,
} from '../../../../components/SideSheetHeader'
import WorkspaceOpportunityDetailsViewModel from '../../WorkspaceOpportunityDetailsViewModel'
import Button from '../../../../components/Button'
import NoOpportunitySelected from '../../../views/OpportunityDetailsSheet/NoOpportunitySelected'
import {
  OpportunityDetailsContentItem,
  OpportunityDetailsContent,
} from '../../../components/OpportunityDetailsContent'
import OpportunityDetailsActions from '../../../components/OpportunityDetailsActions'
import ButtonCollection from '../../../../components/ButtonCollection'

interface WorkspaceOpportunityDetailsSheetProps {
  vm: WorkspaceOpportunityDetailsViewModel
  onCloseClick?: () => void
}

export default observer(function WorkspaceOpportunityDetailsSheet({
  vm,
  onCloseClick,
}: WorkspaceOpportunityDetailsSheetProps) {
  const rootRef = React.useRef<HTMLDivElement | null>(null)
  const scrollOffset = useScrollOffsetSpring(rootRef)
  const opportunity = vm.selected
  return (
    <SideSheet>
      <div className={cx(styles.root)} ref={rootRef}>
        {!opportunity ? (
          <NoOpportunitySelected />
        ) : (
          <>
            <SideSheetHeader
              bordered
              onCloseClick={onCloseClick}
              scrollOffset={scrollOffset}
            >
              <SideSheetHeaderContent>
                <SideSheetHeaderAvatar
                  subject={{
                    avatar: undefined,
                  }}
                />
                <SideSheetHeaderText>
                  <SideSheetHeaderTitle>Firm Opportunity</SideSheetHeaderTitle>
                  <SideSheetHeaderDescription>
                    {opportunity.opportunity.jobTypes.join(', ') ||
                      'No description'}
                  </SideSheetHeaderDescription>
                  <br />
                </SideSheetHeaderText>
                <OpportunityDetailsActions>
                  <ButtonCollection fullWidth>
                    {opportunity.opportunity.isApplicant ? (
                      <Button
                        type="danger"
                        loading={vm.withdraw.pending}
                        disabled={vm.withdraw.pending}
                        onClick={() => vm.withdraw()}
                        dataTest={'workspace-opportunity-withdraw-button'}
                        block
                      >
                        I'm no longer interested
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        loading={vm.apply.pending}
                        disabled={vm.apply.pending || !vm.selectedIsActive}
                        onClick={() => vm.apply()}
                        dataTest={'workspace-opportunity-apply-button'}
                        block
                      >
                        I'm interested
                      </Button>
                    )}
                  </ButtonCollection>
                </OpportunityDetailsActions>
              </SideSheetHeaderContent>
            </SideSheetHeader>
            <Content vm={vm} />
          </>
        )}
      </div>
    </SideSheet>
  )
})

interface ContentProps {
  vm: WorkspaceOpportunityDetailsViewModel
}

const Content = observer(function Content({ vm }: ContentProps) {
  const op = vm.selected
  if (!op) {
    return null
  }

  const children = (
    <>
      <OpportunityDetailsContentItem title="Skills">
        {op.opportunity.specialties.join(', ')}
      </OpportunityDetailsContentItem>
      <OpportunityDetailsContentItem title="Description">
        {op.opportunity.description}
      </OpportunityDetailsContentItem>
      <OpportunityDetailsContentItem title="Software Requirements">
        {op.opportunity.requirements.join(', ')}
      </OpportunityDetailsContentItem>
      {op.opportunity.location && (
        <OpportunityDetailsContentItem title="Location">
          {op.opportunity.location}
        </OpportunityDetailsContentItem>
      )}
    </>
  )

  return (
    <OpportunityDetailsContent>
      {children}
      {/*
        There should always be children because some fields are required.
       */}
    </OpportunityDetailsContent>
  )
})
